import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { tap, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { mobileLogger } from './mobile-logger';

const SCOPE = 'HTTP';

function redactHeaders(headers: Record<string, string | string[]>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(headers)) {
    const value = Array.isArray(raw) ? raw.join(', ') : raw;
    if (/auth-token|authorization|cookie/i.test(key)) {
      out[key] = value ? '***' : '';
    } else {
      out[key] = value;
    }
  }
  return out;
}

function summarizeBody(body: unknown): unknown {
  if (body == null) {
    return null;
  }
  if (typeof body === 'string') {
    return body.length > 256 ? `${body.slice(0, 256)}…` : body;
  }
  if (typeof body !== 'object') {
    return body;
  }
  const record = body as Record<string, unknown>;
  const copy: Record<string, unknown> = { ...record };
  if (typeof copy['otp'] === 'string') {
    copy['otp'] = '******';
  }
  if (typeof copy['otpSecondary'] === 'string') {
    copy['otpSecondary'] = '******';
  }
  if (typeof copy['authToken'] === 'string') {
    copy['authToken'] = '***';
  }
  if (typeof copy['token'] === 'string') {
    copy['token'] = '***';
  }
  return copy;
}

export const apiLoggingInterceptor: HttpInterceptorFn = (req, next) => {
  const started = performance.now();
  const reqHeaders = redactHeaders(
    req.headers.keys().reduce<Record<string, string>>((acc, key) => {
      const v = req.headers.get(key);
      if (v != null) {
        acc[key] = v;
      }
      return acc;
    }, {}),
  );

  mobileLogger.info(SCOPE, '→ request', {
    method: req.method,
    url: req.url,
    withCredentials: req.withCredentials,
    headers: reqHeaders,
    body: summarizeBody(req.body),
    production: environment.production,
    backendUrl: environment.backendUrl,
  });

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        mobileLogger.info(SCOPE, '← response', {
          method: req.method,
          url: req.url,
          status: event.status,
          statusText: event.statusText,
          durationMs: Math.round(performance.now() - started),
          bodyPreview: summarizeBody(event.body),
        });
      }
    }),
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        mobileLogger.error(SCOPE, '✗ http error', {
          method: req.method,
          url: req.url,
          status: err.status,
          statusText: err.statusText,
          durationMs: Math.round(performance.now() - started),
          error: summarizeBody(err.error),
          message: err.message,
          hint:
            err.status === 0
              ? 'Network failure — on USB device use: adb reverse tcp:8090 tcp:8090 (or set LAN IP in environment.prod.ts)'
              : undefined,
        });
      } else {
        mobileLogger.error(SCOPE, '✗ unknown error', {
          method: req.method,
          url: req.url,
          durationMs: Math.round(performance.now() - started),
          error: String(err),
        });
      }
      return throwError(() => err);
    }),
  );
};
